import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, History, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { objectsApi } from "@/services/api-v2.service";
import type { GeometryAuditEntry } from "@/types/api-v2";
import { useToast } from "@/hooks/use-toast";

const ACTION_LABEL: Record<string, string> = {
  create: "Создание",
  update: "Изменение",
  delete: "Удаление",
  card_attrs: "Карточка (атрибуты)",
};

const FILTER_OPTIONS = [
  { id: "all" as const, label: "Все" },
  { id: "create" as const, label: "Создание" },
  { id: "update" as const, label: "Изменение" },
  { id: "delete" as const, label: "Удаление" },
  { id: "card_attrs" as const, label: "Карточка" },
];

type ActionFilter = (typeof FILTER_OPTIONS)[number]["id"];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function actorLabel(row: GeometryAuditEntry): string {
  const u = row.user_username?.trim();
  if (u) return u;
  const e = row.user_email?.trim();
  if (e) return e;
  if (row.user_id) return `${row.user_id.slice(0, 10)}…`;
  return "система";
}

function changedKeysLine(action: string, payload: Record<string, unknown>): string | null {
  if (action !== "update") return null;
  const ck = payload.changed_keys;
  if (!Array.isArray(ck) || ck.length === 0) return null;
  return `Изменено: ${ck.map(String).join(", ")}`;
}

function cardAttrsLine(payload: Record<string, unknown>): string | null {
  const fk = payload.field_keys;
  if (!Array.isArray(fk) || fk.length === 0) return null;
  return `Поля: ${fk.map(String).join(", ")}`;
}

function summaryLine(action: string, payload: Record<string, unknown>): string | null {
  return changedKeysLine(action, payload) ?? (action === "card_attrs" ? cardAttrsLine(payload) : null);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function GeometryAuditCollapsible({ geometryId }: { geometryId: number }) {
  const [open, setOpen] = useState(false);
  const [actionFilter, setActionFilter] = useState<ActionFilter>("all");
  const { toast } = useToast();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["geometry-audit", geometryId],
    queryFn: () => objectsApi.getGeometryAudit(geometryId, 100),
    enabled: open,
  });

  const filtered = useMemo(() => {
    if (!data?.length) return [];
    if (actionFilter === "all") return data;
    return data.filter((r) => r.action === actionFilter);
  }, [data, actionFilter]);

  const exportAudit = () => {
    if (!filtered.length) return;
    const blob = new Blob([JSON.stringify(filtered, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `geometry-${geometryId}-audit.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Экспорт", description: "Файл журнала скачан" });
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-1.5">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-full justify-start gap-1.5 px-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <History className="h-3 w-3 shrink-0" />
          <span>История изменений</span>
          <ChevronDown
            className={`h-3 w-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1.5 text-xs text-muted-foreground border-t border-dashed mt-1.5 pt-2">
        {isLoading && <p>Загрузка…</p>}
        {isError && <p className="text-destructive">Не удалось загрузить журнал</p>}
        {!isLoading && !isError && data && data.length === 0 && (
          <p>Записей пока нет (создание и правки после обновления сервера).</p>
        )}
        {!isLoading && data && data.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-1">
              {FILTER_OPTIONS.map((opt) => (
                <Button
                  key={opt.id}
                  type="button"
                  variant={actionFilter === opt.id ? "secondary" : "ghost"}
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => setActionFilter(opt.id)}
                >
                  {opt.label}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-[10px] ml-auto"
                disabled={!filtered.length}
                onClick={exportAudit}
                title="Скачать отфильтрованные записи как JSON"
              >
                <Download className="h-3 w-3" />
                JSON
              </Button>
            </div>
            {filtered.length === 0 ? (
              <p className="text-[11px]">Нет записей для выбранного фильтра.</p>
            ) : (
              <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {filtered.map((row) => {
                  const extra = summaryLine(row.action, row.payload);
                  const raw = JSON.stringify(row.payload ?? {}, null, 2);
                  return (
                    <li
                      key={row.id}
                      className="rounded border border-border/60 bg-muted/30 px-2 py-1"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-medium text-foreground">
                          {ACTION_LABEL[row.action] ?? row.action}
                        </span>
                        <span className="text-[11px]">{formatDate(row.created_at)}</span>
                      </div>
                      <div
                        className="text-[11px] mt-0.5 truncate"
                        title={row.user_id ?? undefined}
                      >
                        {actorLabel(row)}
                      </div>
                      {extra && (
                        <div className="text-[10px] mt-0.5 opacity-80 leading-tight">
                          {extra}
                        </div>
                      )}
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] text-foreground/70 list-none flex items-center gap-1">
                          <span className="select-none">payload</span>
                        </summary>
                        <pre className="mt-1 max-h-24 overflow-auto rounded bg-background/80 p-1.5 text-[10px] leading-tight text-foreground/90 border border-border/50 whitespace-pre-wrap break-all">
                          {raw}
                        </pre>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 mt-1 gap-1 px-2 text-[10px]"
                          onClick={async () => {
                            const ok = await copyText(raw);
                            toast(
                              ok
                                ? { title: "Скопировано", description: "JSON payload в буфере" }
                                : {
                                    title: "Буфер",
                                    description: "Не удалось скопировать",
                                    variant: "destructive",
                                  },
                            );
                          }}
                        >
                          <Copy className="h-3 w-3" />
                          Копировать JSON
                        </Button>
                      </details>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
